(() => {
  if (customElements.get('blushed-collection')) return;
  class BlushedCollection extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.generation = 0;
      this.busy = false;
      this.loaded = new Set();
      this.homePath = this.dataset.home === 'true' ? location.pathname : null;
      this.controller = new AbortController();
      this.refreshReferences();
      this.source = this.sourceForLocation(new URL(location.href));
      this.reflectState();
      this.uiController = new AbortController();
      document.addEventListener('click', event => {
        const menu = this.querySelector('[data-filter-menu]');
        if (menu?.open && !menu.contains(event.target)) menu.open = false;
      }, { signal: this.uiController.signal });
      this.addEventListener('keydown', event => {
        const menu = this.querySelector('[data-filter-menu]');
        if (event.key === 'Escape' && menu?.open) {
          event.preventDefault();
          event.stopPropagation();
          menu.open = false;
          menu.querySelector('summary')?.focus();
        }
      });
      this.addEventListener('click', event => {
        if (event.target.closest('[data-scroll-collections]')) {
          const tabs = this.querySelector('.bj-tabs');
          tabs?.scrollBy({ left: tabs.clientWidth * 0.8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
        const category = event.target.closest('a[data-category]');
        if (category && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
          event.preventDefault();
          const target = new URL(category.href);
          target.searchParams.set('sort_by', this.sort.value);
          if (this.metal.value !== 'all') target.searchParams.set('blushed_metal', this.metal.value);
          this.replaceCollection(target, true);
        }
        if (event.target.closest('[data-load-more]')) { event.preventDefault(); this.loadMore(); }
        if (event.target.closest('[data-reset]')) {
          event.preventDefault();
          const target = new URL(this.dataset.allUrl, location.origin);
          target.searchParams.set('sort_by', 'created-descending');
          this.replaceCollection(target, true);
        }
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-sort-filter]')) {
          const target = new URL(this.source);
          target.searchParams.set('sort_by', this.sort.value);
          target.searchParams.delete('page');
          this.replaceCollection(target, true);
        }
        if (event.target.matches('[data-metal-filter]')) {
          this.source.searchParams.set('blushed_metal', this.metal.value);
          this.updateHistory(false);
          this.applyFilters();
          if (this.visibleCount() < 12 && this.next) this.loadMore();
        }
      });
      this.addEventListener('submit', event => {
        if (event.target.matches('[data-controls]')) event.preventDefault();
      });
      this.onPopState = () => this.replaceCollection(this.sourceForLocation(new URL(location.href)), false);
      window.addEventListener('popstate', this.onPopState);
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.loadMore();
      }, { rootMargin: '0px 0px 350px 0px' });
      this.observeSentinel();
      const current = new URL(location.href);
      if (this.dataset.home === 'true' || !current.searchParams.has('sort_by')) this.replaceCollection(this.source, false);
      else {
        this.next = this.normalizedNext(this.dataset.nextUrl);
        this.applyFilters();
        if (this.visibleCount() < 12 && this.next) this.loadMore();
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.uiController?.abort();
      this.navController?.abort();
      this.navResizeObserver?.disconnect();
      this.observer?.disconnect();
      window.removeEventListener('popstate', this.onPopState);
    }
    refreshReferences() {
      this.grid = this.querySelector('[data-products]');
      this.sort = this.querySelector('[data-sort-filter]');
      this.metal = this.querySelector('[data-metal-filter]');
      this.status = this.querySelector('[data-load-status]');
      this.more = this.querySelector('[data-load-more]');
      this.empty = this.querySelector('[data-empty]');
      this.navController?.abort();
      this.navResizeObserver?.disconnect();
      this.navController = new AbortController();
      const tabs = this.querySelector('.bj-tabs');
      const arrow = this.querySelector('[data-scroll-collections]');
      if (tabs && arrow) {
        const updateArrow = () => { arrow.disabled = tabs.scrollWidth - tabs.clientWidth - tabs.scrollLeft <= 1; };
        tabs.addEventListener('scroll', updateArrow, { passive: true, signal: this.navController.signal });
        this.navResizeObserver = new ResizeObserver(updateArrow);
        this.navResizeObserver.observe(tabs);
        updateArrow();
      }
    }
    sourceForLocation(url) {
      const isHome = this.homePath && url.pathname === this.homePath;
      const source = isHome ? new URL(this.dataset.collectionUrl, location.origin) : new URL(url);
      for (const key of ['sort_by', 'blushed_kind', 'blushed_metal', 'preview_theme_id']) {
        const value = url.searchParams.get(key);
        if (value) source.searchParams.set(key, value);
      }
      if (!source.searchParams.has('sort_by')) source.searchParams.set('sort_by', 'created-descending');
      source.searchParams.delete('section_id');
      return source;
    }
    normalizedNext(value) {
      if (!value) return null;
      const next = new URL(value, this.source);
      if (next.origin !== location.origin) return null;
      for (const key of ['sort_by','blushed_kind','blushed_metal','preview_theme_id']) {
        const current = this.source.searchParams.get(key);
        if (current) next.searchParams.set(key, current);
      }
      next.searchParams.delete('section_id');
      return next;
    }
    async fetchCollection(source) {
      const url = new URL(source);
      if (url.origin !== location.origin) throw new Error('Invalid collection');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('section_id', 'blushed-grid');
      const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Collection unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = doc.querySelector('blushed-collection');
      if (!result) throw new Error('Collection unavailable');
      return result;
    }
    updateHistory(push) {
      const target = new URL(this.source);
      target.searchParams.delete('section_id');
      target.searchParams.delete('page');
      if (target.searchParams.get('blushed_metal') === 'all') target.searchParams.delete('blushed_metal');
      const method = push ? 'pushState' : 'replaceState';
      history[method]({ blushed: true }, '', target.pathname + target.search + target.hash);
    }
    async replaceCollection(target, push) {
      const source = new URL(target);
      this.failedSource = null;
      if (source.origin !== location.origin) return;
      if (!source.searchParams.has('sort_by')) source.searchParams.set('sort_by', 'created-descending');
      const filtersHadFocus = this.querySelector('[data-filter-menu]')?.contains(document.activeElement);
      this.controller.abort();
      this.controller = new AbortController();
      const generation = ++this.generation;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading jewelry…';
      try {
        const result = await this.fetchCollection(source);
        if (generation !== this.generation) return;
        this.innerHTML = result.innerHTML;
        this.dataset.collectionUrl = result.dataset.collectionUrl;
        this.source = source;
        this.loaded = new Set([source.href]);
        this.next = this.normalizedNext(result.dataset.nextUrl);
        this.refreshReferences();
        this.reflectState();
        this.applyFilters();
        this.observeSentinel();
        if (filtersHadFocus) this.querySelector('[data-filter-menu] summary')?.focus({ preventScroll: true });
        if (push) {
          this.updateHistory(true);
          this.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      } catch (error) {
        if (error.name !== 'AbortError' && generation === this.generation) {
          this.status.textContent = 'The collection couldn’t load. Please try again.';
          this.more.hidden = false;
          this.more.href = source.href;
          this.more.textContent = 'Try again ↗';
          this.failedSource = source;
        }
      } finally {
        if (generation === this.generation) {
          this.busy = false;
          this.removeAttribute('aria-busy');
          if (this.visibleCount() < 12 && this.next && !this.failedSource) this.loadMore();
        }
      }
    }
    reflectState() {
      this.sort.value = this.source.searchParams.get('sort_by') || 'created-descending';
      this.metal.value = this.source.searchParams.get('blushed_metal') || 'all';
      this.kind = this.source.searchParams.get('blushed_kind') || '';
      this.querySelectorAll('[data-category]').forEach(link => {
        const url = new URL(link.href);
        const selected = url.pathname === this.source.pathname && (url.searchParams.get('blushed_kind') || '') === this.kind;
        if (selected) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
      });
    }
    visibleCount() { return [...this.grid.querySelectorAll('.bj-card')].filter(card => !card.hidden).length; }
    applyFilters() {
      this.grid.querySelectorAll('.bj-card').forEach(card => {
        card.hidden = (this.metal.value !== 'all' && card.dataset.metal !== this.metal.value) || (this.kind && !card.dataset.kinds.split(' ').includes(this.kind));
      });
      const count = this.visibleCount();
      this.empty.hidden = count > 0 || Boolean(this.next);
      this.querySelector('[data-results]').textContent = `${count} pieces loaded`;
      this.querySelector('.bj-reset').hidden = this.metal.value === 'all' && !this.kind && this.source.pathname === new URL(this.dataset.allUrl, location.origin).pathname;
      this.more.hidden = !this.next;
      if (this.next) this.more.href = this.next.href;
      this.status.textContent = '';
    }
    observeSentinel() {
      this.observer?.disconnect();
      const sentinel = this.querySelector('[data-sentinel]');
      if (sentinel) this.observer?.observe(sentinel);
    }
    async loadMore() {
      if (this.busy) return;
      if (this.failedSource) {
        const source = this.failedSource; this.failedSource = null;
        return this.replaceCollection(source, false);
      }
      if (!this.next) return;
      const generation = this.generation;
      const targetCount = this.visibleCount() + 12;
      let scannedPages = 0;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading more jewelry…';
      try {
        while (this.next && this.visibleCount() < targetCount && scannedPages < 5) {
          const source = this.next;
          if (this.loaded.has(source.href)) { this.next = null; break; }
          const result = await this.fetchCollection(source);
          if (generation !== this.generation) return;
          this.loaded.add(source.href);
          const ids = new Set([...this.grid.querySelectorAll('[data-product-id]')].map(card => card.dataset.productId));
          result.querySelectorAll('[data-products] > .bj-card').forEach(card => {
            if (!ids.has(card.dataset.productId)) this.grid.append(card);
          });
          this.next = this.normalizedNext(result.dataset.nextUrl);
          this.applyFilters();
          scannedPages++;
        }
        this.status.textContent = '';
      } catch (error) {
        if (error.name !== 'AbortError' && generation === this.generation) this.status.textContent = 'More pieces couldn’t load. Select “Show more pieces” to try again.';
      } finally {
        if (generation === this.generation) {
          this.busy = false;
          this.removeAttribute('aria-busy');
          this.more.hidden = !this.next;
          const top = this.querySelector('[data-sentinel]')?.getBoundingClientRect().top;
          if (this.next && !this.status.textContent && top < innerHeight + 350) requestAnimationFrame(() => this.loadMore());
        }
      }
    }
  }
  customElements.define('blushed-collection', BlushedCollection);
})();
